import {
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextFieldProps,
    FormControlProps,
    FormHelperText
} from '@mui/material';
import { useState } from 'react';
import {
    ApprovalType,
    CreateCompanyRequest,
    LoginType,
    LowBalanceAudience,
    PaymentSource,
    approvalTypes,
    getLowBalanceAudienceLabel
} from '@/models/company';
import Autocomplete from '@mui/material/Autocomplete';
import countryList from 'react-select-country-list';

import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { Validator } from '@/utils/errorHandling';
import { MuiTelInput } from 'mui-tel-input';
import {
    PaymentProvider,
    PaymentSettingsCombination,
    paymentSettingsCombinations
} from '@/models/payment';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { currencyList } from '@/utils/currencyList';
import {
    useSalaryProvidersQuery,
    useTimeRegistrationProvidersQuery
} from '@/services/paymentsQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { NumericInput } from '@/components/shared/NumericInput';

type AddCompanyDialogProps = {
    open: boolean;
    onClose: () => void;
    onAdd: (
        request: CreateCompanyRequest,
        onSuccess: () => void
    ) => Promise<void>;
    validator: Validator;
    setValidator: React.Dispatch<React.SetStateAction<Validator>>;
    isLoading?: boolean;
};

export const AddCompanyDialog = (props: AddCompanyDialogProps) => {
    const sortedCurrency = currencyList.sort((a, b) => a?.localeCompare(b));

    const currencyOptions = sortedCurrency.map((item) => {
        return { label: item, value: item };
    });

    const countryOptions = countryList().getData();

    const defaultPricePerUser = 19;
    const getInitialEndOfTrial = () => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 1, 0, 0, 0, 0); // end of current month
    };

    const [companyName, setCompanyName] = useState<string>('');
    const [currency, setCurrency] = useState<string>('DKK');
    const [country, setCountry] = useState<string>('Denmark');
    const [tenantId, setTenantId] = useState<string>('');
    const [loginType, setLoginType] = useState<LoginType>(LoginType.AzureB2C);
    const [salaryProvider, setSalaryProvider] = useState<string>('Zenegy');
    const [timeRegistrationProvider, setTimeRegistrationProvider] =
        useState<string>('Zenegy');
    const [pricePerUser, setPricePerUser] = useState<number | null>(
        defaultPricePerUser
    );
    const [employeeWithdrawalFee, setEmployeeWithdrawalFee] = useState<
        number | null
    >(0);
    const [companyServiceFee, setCompanyServiceFee] = useState<number | null>(
        0
    );
    const [endOfTrial, setEndOfTrial] = useState<Date | null>(
        getInitialEndOfTrial()
    );
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [paymentCombination, setPaymentCombination] =
        useState<PaymentSettingsCombination>('InstaPaid');
    const [paymentProvider, setPaymentProvider] =
        useState<PaymentProvider>('None');
    const [paymentSource, setPaymentSource] =
        useState<PaymentSource>('InstaPaidAccount');
    const [approvalType, setApprovalType] = useState<ApprovalType>('Manual');
    const [lowBalanceNotification, setLowBalanceNotification] = useState<
        number | null
    >(null);
    const [lowBalanceAudience, setLowBalanceAudience] =
        useState<LowBalanceAudience>('PendingWithdrawalsEmails');
    const [lowBalanceEmail, setLowBalanceEmail] = useState<string>('');
    const [pricePerUserFormatError, setPricePerUserFormatError] =
        useState(false);
    const [withdrawalFeeFormatError, setWithdrawalFeeFormatError] =
        useState(false);
    const [serviceFeeFormatError, setServiceFeeFormatError] = useState(false);
    const [lowBalanceFormatError, setLowBalanceFormatError] = useState(false);
    const hasFormatError =
        pricePerUserFormatError ||
        withdrawalFeeFormatError ||
        serviceFeeFormatError ||
        lowBalanceFormatError;
    const { setAlert } = useNotificationsSnackbar();

    const setPaymentCombinationWithDependancies = (
        newPaymentCombination: PaymentSettingsCombination
    ) => {
        setPaymentCombination(newPaymentCombination);
        if (newPaymentCombination === 'Danske Bank') {
            setPaymentProvider('DanskeBank');
            setPaymentSource('CompanyAccount');
        } else if (newPaymentCombination === 'InstaPaid') {
            setPaymentProvider('None');
            setPaymentSource('InstaPaidAccount');
        } else if (newPaymentCombination === 'Aiia') {
            setPaymentProvider('Aiia');
            setPaymentSource('CompanyAccount');
            setApprovalType('Manual'); // automatic approvals are not available for Aiia
        }
    };

    const dateToUTC = (date: Date) => {
        // make sure we send the data the user sees in the datepicker, ignoring the possible timezone offset
        return new Date(
            Date.UTC(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                17,
                0,
                0
            )
        );
    };

    const clearState = () => {
        setCompanyName('');
        setCurrency('');
        setCountry('');
        setTenantId('');
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhoneNumber('');
        setLowBalanceNotification(null);
        setLowBalanceEmail('');
    };

    const onAdd = () => {
        if (
            !companyName ||
            !currency ||
            !country ||
            !loginType ||
            !firstName ||
            !lastName ||
            !email ||
            !endOfTrial
        ) {
            setAlert(t('company.please_fill_all_fields'), 'error');
            return;
        }
        const lowBalanceNotificationDecimal = lowBalanceNotification;
        const pricePerUserDecimal = pricePerUser;
        const employeeWithdrawalFeeDecimal = employeeWithdrawalFee;
        const companyServiceFeeDecimal = companyServiceFee;

        if (lowBalanceNotificationDecimal === undefined) {
            setAlert(t('company.low_balance_must_be_number'), 'error');
            return;
        }

        if (pricePerUserDecimal === undefined) {
            setAlert('Price per user must be a number', 'error');
            return;
        }

        props.onAdd(
            {
                name: companyName,
                currency: currency,
                country: country,
                tenantId: tenantId,
                loginType: loginType,
                salaryProvider: salaryProvider,
                timeRegistrationProvider: timeRegistrationProvider,
                pricePerUser: pricePerUserDecimal ?? defaultPricePerUser,
                employeeWithdrawalFee: employeeWithdrawalFeeDecimal ?? 0,
                companyServiceFee: companyServiceFeeDecimal ?? 0,
                endOfTrial: dateToUTC(endOfTrial),
                paymentSource: paymentSource,
                paymentProvider: paymentProvider,
                approvalType: approvalType,
                lowBalanceNotificationThreshold: lowBalanceNotificationDecimal,
                lowBalanceAudience: lowBalanceAudience,
                lowBalanceEmail: lowBalanceEmail,
                admin: {
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    phoneNumber: phoneNumber
                }
            },
            clearState
        );
    };

    const commonProps: TextFieldProps = {
        margin: 'dense',
        fullWidth: true,
        variant: 'standard'
    };

    const { data: getTimeRegistrationProvidersQuery } =
        useTimeRegistrationProvidersQuery(props.open);

    const { data: getSalaryProvidersQuery } = useSalaryProvidersQuery(
        props.open
    );

    const { t } = useTranslation();

    return (
        <Dialog open={props.open} onClose={props.onClose}>
            <DialogTitle>Add new company</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Fill the fields below to create a new company and the first
                    admin user for this company
                </DialogContentText>

                <TextField
                    {...commonProps}
                    label="Company name"
                    value={companyName}
                    error={props.validator.hasError('companyName')}
                    helperText={props.validator.getError('companyName')}
                    onChange={(e) => {
                        setCompanyName(e.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('companyName')
                        );
                    }}
                />

                {
                    <Autocomplete
                        id="currency"
                        isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                        }
                        options={currencyOptions}
                        value={currency as any}
                        onChange={(event, value) => {
                            setCurrency(value?.label || '');
                            props.setValidator(
                                props.validator.clearError('currency')
                            );
                        }}
                        renderInput={(params: any): any => (
                            <TextField
                                {...commonProps}
                                {...params}
                                error={props.validator.hasError('currency')}
                                helperText={props.validator.getError(
                                    'currency'
                                )}
                                label="Currency"
                            />
                        )}
                    />
                }
                <Autocomplete
                    id="country"
                    isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                    }
                    options={countryOptions}
                    value={country as any}
                    onChange={(event, value) => {
                        setCountry(value?.label || '');
                        props.setValidator(
                            props.validator.clearError('country')
                        );
                    }}
                    renderInput={(params: any): any => (
                        <TextField
                            {...commonProps}
                            {...params}
                            error={props.validator.hasError('country')}
                            helperText={props.validator.getError('country')}
                            label="Country"
                        />
                    )}
                />
                <FormControl {...(commonProps as FormControlProps)}>
                    <InputLabel>Login type</InputLabel>
                    <Select
                        variant="standard"
                        value={loginType}
                        label="Login type"
                        error={props.validator.hasError('loginType')}
                        onChange={(e) => {
                            setLoginType(e.target.value as LoginType);
                            props.setValidator(
                                props.validator.clearError('loginType')
                            );
                        }}
                    >
                        <MenuItem value={LoginType.AzureAD}>
                            Company login
                        </MenuItem>
                        <MenuItem value={LoginType.AzureB2C}>
                            Instapaid login
                        </MenuItem>
                    </Select>
                    <FormHelperText>
                        {props.validator.getError('loginType')}
                    </FormHelperText>
                </FormControl>

                {loginType === LoginType.AzureAD && (
                    <TextField
                        {...commonProps}
                        label={`${LoginType[loginType]} Tenant ID`}
                        value={tenantId}
                        error={props.validator.hasError('tenantId')}
                        helperText={props.validator.getError('tenantId')}
                        onChange={(e) => {
                            setTenantId(e.currentTarget.value);
                            props.setValidator(
                                props.validator.clearError('tenantId')
                            );
                        }}
                    />
                )}

                <FormControl {...(commonProps as FormControlProps)}>
                    <InputLabel>Salary provider</InputLabel>
                    <Select
                        variant="standard"
                        value={salaryProvider}
                        label="Salary provider"
                        error={props.validator.hasError('salaryProvider')}
                        onChange={(e) => {
                            setSalaryProvider(e.target.value);
                            props.setValidator(
                                props.validator.clearError('salaryProvider')
                            );
                        }}
                    >
                        {getSalaryProvidersQuery?.data?.salaryProviders.map(
                            (salaryProvider) => (
                                <MenuItem
                                    key={salaryProvider.name}
                                    value={salaryProvider.name}
                                >
                                    {salaryProvider.name}
                                </MenuItem>
                            )
                        )}
                    </Select>
                    <FormHelperText>
                        {props.validator.getError('salaryProvider')}
                    </FormHelperText>
                </FormControl>

                <FormControl {...(commonProps as FormControlProps)}>
                    <InputLabel>Time registration provider</InputLabel>
                    <Select
                        variant="standard"
                        value={timeRegistrationProvider}
                        label="Time registration provider"
                        error={props.validator.hasError(
                            'timeRegistrationProvider'
                        )}
                        onChange={(e) => {
                            setTimeRegistrationProvider(e.target.value);
                            props.setValidator(
                                props.validator.clearError(
                                    'timeRegistrationProvider'
                                )
                            );
                        }}
                    >
                        {getTimeRegistrationProvidersQuery?.data?.providers?.map(
                            (timeRegistrationProvider) => (
                                <MenuItem
                                    key={timeRegistrationProvider.name}
                                    value={timeRegistrationProvider.name}
                                >
                                    {timeRegistrationProvider.name}
                                </MenuItem>
                            )
                        )}
                    </Select>
                    <FormHelperText>
                        {props.validator.getError('timeRegistrationProvider')}
                    </FormHelperText>
                </FormControl>

                <NumericInput
                    fullWidth
                    margin="dense"
                    label={t('price_per_user')}
                    helperText={props.validator.getError(
                        'pricePerUser',
                        t('price_for_user_subtitle')
                    )}
                    error={props.validator.hasError('pricePerUser')}
                    value={pricePerUser}
                    onChange={(val) => {
                        setPricePerUser(val);
                        props.setValidator(
                            props.validator.clearError('pricePerUser')
                        );
                    }}
                    onFormatError={setPricePerUserFormatError}
                />

                <NumericInput
                    fullWidth
                    margin="dense"
                    label={t('fees.withdrawal_fee')}
                    helperText={props.validator.getError(
                        'employeeWithdrawalFee',
                        t('fees.withdrawal_fee_subtitle')
                    )}
                    error={props.validator.hasError('employeeWithdrawalFee')}
                    value={employeeWithdrawalFee}
                    onChange={(val) => {
                        setEmployeeWithdrawalFee(val);
                        props.setValidator(
                            props.validator.clearError('employeeWithdrawalFee')
                        );
                    }}
                    onFormatError={setWithdrawalFeeFormatError}
                />
                <NumericInput
                    fullWidth
                    margin="dense"
                    label={t('fees.monthly_service_fee')}
                    helperText={props.validator.getError(
                        'companyServiceFee',
                        t('fees.base_fee')
                    )}
                    error={props.validator.hasError('companyServiceFee')}
                    value={companyServiceFee}
                    onChange={(val) => {
                        setCompanyServiceFee(val);
                        props.setValidator(
                            props.validator.clearError('companyServiceFee')
                        );
                    }}
                    onFormatError={setServiceFeeFormatError}
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        slotProps={{
                            textField: {
                                ...commonProps,
                                error: props.validator.hasError('endOfTrial'),
                                helperText:
                                    props.validator.getError('endOfTrial')
                            }
                        }}
                        label="End of trial"
                        value={dayjs(endOfTrial)}
                        minDate={dayjs()}
                        format="DD/MM/YYYY"
                        onChange={(newValue: Dayjs | null) => {
                            setEndOfTrial(
                                newValue
                                    ? newValue.toDate()
                                    : getInitialEndOfTrial()
                            );
                            props.setValidator(
                                props.validator.clearError('endOfTrial')
                            );
                        }}
                    />
                </LocalizationProvider>

                <FormControl
                    {...(commonProps as FormControlProps)}
                    error={
                        props.validator.hasError('paymentSource') ||
                        props.validator.hasError('paymentProvider')
                    }
                >
                    <InputLabel>Payout method</InputLabel>
                    <Select
                        variant="standard"
                        value={paymentCombination}
                        label="Payout method"
                        onChange={(e) => {
                            setPaymentCombinationWithDependancies(
                                e.target.value as PaymentSettingsCombination
                            );
                            props.setValidator(
                                props.validator.clearError('paymentSource')
                            );
                            props.setValidator(
                                props.validator.clearError('paymentProvider')
                            );
                        }}
                    >
                        {paymentSettingsCombinations.map((item) => (
                            <MenuItem key={item} value={item}>
                                {item}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {props.validator.getError('paymentSource')}
                        {props.validator.getError('paymentProvider')}
                    </FormHelperText>
                </FormControl>

                {paymentCombination !== 'Aiia' && (
                    <FormControl
                        {...(commonProps as FormControlProps)}
                        error={props.validator.hasError('approvalType')}
                    >
                        <InputLabel>Approval type</InputLabel>
                        <Select
                            variant="standard"
                            value={approvalType}
                            label="Approval type"
                            onChange={(e) => {
                                setApprovalType(e.target.value as ApprovalType);
                                props.setValidator(
                                    props.validator.clearError('approvalType')
                                );
                            }}
                        >
                            {approvalTypes.map((item) => (
                                <MenuItem key={item} value={item}>
                                    {item}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            {props.validator.getError(
                                'approvalType',
                                "If you choose manual, you'll need to manually approve each withdrawal request made by your employees. Auto-approvals are only supported by DanskeBank."
                            )}
                        </FormHelperText>
                    </FormControl>
                )}

                {paymentSource === 'InstaPaidAccount' && (
                    <>
                        <NumericInput
                            fullWidth
                            margin="dense"
                            label={t('company.low_balance_notification')}
                            helperText={props.validator.getError(
                                'lowBalanceNotification',
                                "If the company balance reaches below this number, you'll receive an email remainder to deposit funds"
                            )}
                            error={props.validator.hasError(
                                'lowBalanceNotification'
                            )}
                            value={lowBalanceNotification}
                            onChange={(val) => {
                                setLowBalanceNotification(val);
                                props.setValidator(
                                    props.validator.clearError(
                                        'lowBalanceNotification'
                                    )
                                );
                            }}
                            onFormatError={setLowBalanceFormatError}
                        />

                        <FormControl
                            {...(commonProps as FormControlProps)}
                            error={props.validator.hasError(
                                'lowBalanceAudience'
                            )}
                        >
                            <InputLabel>
                                Low balance notification audience
                            </InputLabel>
                            <Select
                                variant="standard"
                                value={lowBalanceAudience}
                                label="Low balance notification audience"
                                onChange={(e) => {
                                    setLowBalanceAudience(
                                        e.target.value as LowBalanceAudience
                                    );
                                    props.setValidator(
                                        props.validator.clearError(
                                            'lowBalanceAudience'
                                        )
                                    );
                                }}
                            >
                                {(
                                    [
                                        'AllAdmins',
                                        'PendingWithdrawalsEmails',
                                        'Custom'
                                    ] as LowBalanceAudience[]
                                ).map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {getLowBalanceAudienceLabel(item)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {props.validator.getError(
                                    'lowBalanceAudience',
                                    'Choose who should receive emails about your company account balance running low'
                                )}
                            </FormHelperText>
                        </FormControl>

                        {lowBalanceAudience === 'Custom' && (
                            <TextField
                                {...commonProps}
                                label="Emails to receive the notification about low company balance (comma-separated)"
                                helperText={props.validator.getError(
                                    'lowBalanceEmail',
                                    'This email address(-es) will receive the email notifications if your company balance reaches below the threshold above'
                                )}
                                error={props.validator.hasError(
                                    'lowBalanceEmail'
                                )}
                                value={lowBalanceEmail}
                                onChange={(e) => {
                                    setLowBalanceEmail(e.currentTarget.value);
                                    props.setValidator(
                                        props.validator.clearError(
                                            'lowBalanceEmail'
                                        )
                                    );
                                }}
                            />
                        )}
                    </>
                )}

                <Typography mt={3} mb={0} fontWeight="bold">
                    Admin user details:
                </Typography>

                <TextField
                    {...commonProps}
                    label="First name"
                    value={firstName}
                    error={props.validator.hasError('admin.FirstName')}
                    helperText={props.validator.getError('admin.FirstName')}
                    onChange={(e) => {
                        setFirstName(e.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('admin.FirstName')
                        );
                    }}
                />

                <TextField
                    {...commonProps}
                    label="Last name"
                    value={lastName}
                    error={props.validator.hasError('admin.LastName')}
                    helperText={props.validator.getError('admin.LastName')}
                    onChange={(e) => {
                        setLastName(e.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('admin.LastName')
                        );
                    }}
                />

                <TextField
                    {...commonProps}
                    label="Email Address"
                    type="email"
                    value={email}
                    error={props.validator.hasError('admin.Email')}
                    helperText={props.validator.getError('admin.Email')}
                    onChange={(e) => {
                        setEmail(e.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('admin.Email')
                        );
                    }}
                />

                <MuiTelInput
                    {...commonProps}
                    name="phone_number"
                    label="Phone number"
                    placeholder="+XX XXXXXX"
                    error={props.validator.hasError('admin.PhoneNumber')}
                    helperText={props.validator.getError(
                        'admin.PhoneNumber',
                        'Phone number including country code'
                    )}
                    value={phoneNumber}
                    onChange={(e) => {
                        setPhoneNumber(e);
                        props.setValidator(
                            props.validator.clearError('admin.PhoneNumber')
                        );
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} disabled={props.isLoading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={onAdd}
                    disabled={props.isLoading || hasFormatError}
                >
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
};
